import { useEffect, useState } from "react";
import { Building2, MapPin, FileText, Check } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Post-login Unit Picker.
 *
 * Renders a fixed-overlay modal asking the admin to pick Unit 01 or Unit 02
 * the first time they log in (or whenever localStorage.uee_unit is cleared).
 * The chosen unit drives the company name + address + GSTIN that appears on
 * every PDF the admin generates in the session.
 *
 * Nothing else in the UI changes — same forms, same workflow, same layouts.
 */
export default function UnitPicker() {
  const { user, activeUnit, setActiveUnit } = useAuth();
  const [units, setUnits] = useState([]);
  const [busy, setBusy] = useState(false);

  // Only show for authenticated users who haven't picked yet
  const open = !!user && !activeUnit;

  useEffect(() => {
    if (!open) return;
    api.get("/units").then((r) => setUnits(r.data)).catch((e) =>
      toast.error(formatApiError(e))
    );
  }, [open]);

  if (!open) return null;

  const choose = (key) => {
    setBusy(true);
    setActiveUnit(key);
    const picked = units.find((u) => u.key === key);
    toast.success(`Active unit: ${picked?.label || key}`);
    setBusy(false);
  };

  return (
    <div
      data-testid="unit-picker-overlay"
      className="fixed inset-0 z-[100] bg-slate-950/85 backdrop-blur-sm grid place-items-center p-4"
    >
      <div className="bg-white border border-slate-200 max-w-2xl w-full shadow-2xl">
        <div className="bg-slate-950 text-white px-6 py-5">
          <div className="overline text-amber-400 mb-1">Select your unit</div>
          <h2 className="font-heading text-xl md:text-2xl font-bold">
            Which unit will you generate documents for?
          </h2>
          <p className="text-xs text-slate-300 mt-2">
            Your selection drives the address &amp; GSTIN that appears on every
            PDF you create in this session. You can change it any time from the
            badge in the top bar.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-0 border-t border-slate-200">
          {units.length === 0 ? (
            <div className="col-span-2 p-10 text-center text-slate-500 text-sm">
              Loading units…
            </div>
          ) : (
            units.map((u) => (
              <button
                key={u.key}
                disabled={busy}
                onClick={() => choose(u.key)}
                data-testid={`pick-${u.key}`}
                className="text-left p-6 border-r last:border-r-0 border-slate-200 hover:bg-amber-50 transition-colors group focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-amber-500" />
                  <div className="overline text-slate-500">{u.key.toUpperCase()}</div>
                </div>
                <div className="font-heading text-lg font-bold text-slate-900 leading-tight mb-3">
                  {u.label}
                </div>
                <div className="flex items-start gap-2 text-xs text-slate-600 mb-2">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
                  <div className="leading-relaxed">{u.address}</div>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-600 font-mono mt-3 pt-3 border-t border-slate-100">
                  <FileText className="w-3.5 h-3.5 text-slate-400" />
                  <span>{u.gst || <em className="text-amber-700">GSTIN pending — admin can update later</em>}</span>
                </div>
                <div className="mt-5 flex items-center gap-2 text-amber-700 text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                  <Check className="w-4 h-4" /> Use this unit
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
